if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this),
        );

        this.initQuantityHandlers();
        this.trackProductViewed();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
        if (this._trackRetryTimeout) {
          clearTimeout(this._trackRetryTimeout);
        }
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html
            .querySelectorAll('.scroll-trigger')
            .forEach((element) => element.classList.add('scroll-trigger--cancel')),
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues, newVariantId } }) {
        if (!this.contains(event.target)) return;

        if (
          target.parentElement.closest('.linked-products-new__variant-options--has-serialized-variants') ||
          target.parentElement.querySelector('.linked-products-new__variant-options--has-serialized-variants')
        ) {
          this.variantsLoading = true;

          const listItems =
            target.parentElement.closest('.linked-products-new__variant-options--has-serialized-variants') ||
            target.parentElement.querySelector('.linked-products-new__variant-options--has-serialized-variants');

          listItems.classList.add('variants-loading');
        }

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;

        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;

        let requestUrl = productUrl;

        if (newVariantId) {
          requestUrl = productUrl + `?variant=${newVariantId}`;
        }

        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(requestUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariantMetafields(productInfoNode) {
        const selectedVariantMetafields = productInfoNode.querySelector(
          'variant-selects [data-selected-variant-metafields]',
        )?.innerHTML;
        return !!selectedVariantMetafields ? JSON.parse(selectedVariantMetafields) : null;
      }
      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;

        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false, variantId) {
        if (variantId) {
          return `${url}`;
        }

        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}${url.includes('?') ? '&' : '?'}${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      getSerialAndWeight(html) {
        const serialNumberInput = html.querySelector('input[id="serial-number"]');
        const weightInput = html.querySelector('input[id="weight"]');
        return { serialNumber: serialNumberInput?.value, weight: weightInput?.value };
      }

      getCustomImageAttribute(html) {
        const customImageAttributeInput = html.querySelector('input[id="custom_image_attribute"]');
        return customImageAttributeInput?.value;
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);
          const metafields = this.getSelectedVariantMetafields(html);

          const { serialNumber, weight } = this.getSerialAndWeight(html);
          const customImageAttribute = this.getCustomImageAttribute(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id, serialNumber, weight, customImageAttribute);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          if (metafields.metafields?.length) {
            this.updateMedia(html, metafields.metafields[0]);
          } else {
            this.updateMedia(html, variant?.featured_media?.id);
          }

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);

            const destination = id.includes('modal')
              ? document.querySelector(`#${id}-${this.dataset.section}`)
              : this.querySelector(`#${id}-${this.dataset.section}`);

            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }

            if (source === null && destination) {
              destination.classList.add('hidden');
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          updateSourceFromDestination('linked-products-new', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('product-new__badge', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('left-product-selector', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('garage-availability', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('variant-selects', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('serialisation-modal', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('lcly-upc');

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut,
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });

          if (this.variantsLoading) {
            this.variantsLoading = false;
            this.querySelectorAll('.variants-loading').forEach((element) =>
              element.classList.remove('variants-loading'),
            );
          }
        };
      }

      updateVariantInputs(variantId, serialNumber, weight, customImageAttribute) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`,
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));

          const serialNumberInput = productForm.querySelector('input[id="serial-number"]');
          if (serialNumberInput) {
            serialNumberInput.value = serialNumber ?? '';
          }

          const weightInput = productForm.querySelector('input[id="weight"]');
          if (weightInput) {
            weightInput.value = weight ?? '';
          }

          const customImageAttributeInput = productForm.querySelector('input[id="custom_image_attribute"]');
          if (customImageAttributeInput) {
            customImageAttributeInput.value = customImageAttribute ?? '';
          }
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`,
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = [
          'price',
          'Inventory',
          'Sku',
          'Price-Per-Item',
          'Volume-Note',
          'Volume',
          'Quantity-Rules',
          'lcly-embed',
        ]
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;
        const mediaGalleryParentDestination = html.querySelector('media-gallery');
        const mediaGalleryParentSource = this.querySelector('media-gallery');
        const mediaGallerySource = this.querySelector('media-gallery ul');

        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));

          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));

          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }]),
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]'),
          );

          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));

          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);

              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();

              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              if (mediaGalleryDestination.dataset.isSerialized) return;

              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`),
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });

          const sliderButtons = mediaGalleryParentDestination.querySelector('.slider-buttons');

          const prevSliderButtons = mediaGalleryParentSource?.querySelector('.slider-buttons');

          /* Replace prevSliderButtons with sliderButtons */
          if (prevSliderButtons && sliderButtons) {
            prevSliderButtons.replaceWith(sliderButtons);
          }
          /* Update the slider dots */
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true,
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);

        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      updateDots(slider) {
        // Update pagination dots
        const dotsContainer = slider.querySelector('.slider-counter');
        if (dotsContainer) {
          // Clear existing dots
          dotsContainer.innerHTML = '';

          // Create new dots based on visible slides
          slider.sliderItemsToShow.forEach((_, index) => {
            const dot = document.createElement('span');
            dot.innerHTML = `<svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
       <circle cx="4" cy="4" r="4" fill="black"></circle>
     </svg>`;
            dot.classList.add('slider-counter--item');
            dot.setAttribute('data-index', index + 1);
            dot.setAttribute('aria-label', `Page ${index + 1}`);

            // Set the first dot as current
            if (index === 0) {
              dot.classList.add('slider-counter--current');
            }

            // Add click handler
            dot.addEventListener('click', (event) => {
              slider.onPaginationDotClick(event, dot);
            });

            dotsContainer.appendChild(dot);
          });
        }
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products',
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list',
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      trackProductViewed(retryCount = 0) {
        // Only track on main product pages, not in modals or quick views
        if (!this.id || !this.id.startsWith('MainProduct-')) {
          return;
        }

        if (this._productViewTracked) return;

        try {
          // Get product data from JSON script tag
          const productDataScript = document.getElementById(`ProductData-${this.dataset.section}`);
          if (!productDataScript) {
            return;
          }

          const productData = JSON.parse(productDataScript.textContent);

          // Check if Mixpanel is available — if not, retry up to 10 times (5s total)
          // to allow OneTrust consent to load and initialize Mixpanel
          if (typeof window.mixpanel === 'undefined' || !window.mixpanel.track) {
            if (retryCount < 10) {
              this._trackRetryTimeout = setTimeout(() => this.trackProductViewed(retryCount + 1), 500);
            }
            return;
          }

          // Skip tracking for bots
          if (typeof isBot === 'function' && isBot()) {
            return;
          }

          // Get market from Shopify global object
          const market = window.Shopify?.market_handle || 'unknown';

          // Track product viewed event with only the required fields
          this._productViewTracked = true;
          mixpanel.track('Product Viewed V.1', {
            'Product ID': productData.product_id,
            'Product Handle': productData.product_handle || '', // canonical, language-stable product key
            'Variant ID': productData.variant_id,
            SKU: productData.sku,
            // Standardized (canonical metaobject handle, identical across languages);
            // the localized label is kept separately for display/QA.
            'Product Type': productData.product_type_handle || productData.product_type,
            'Product Type (Display)': productData.product_type,
            'Product Title': productData.product_title,
            Brand: productData.brand,
            Price: productData.price / 100, // Convert from cents to dollars
            Categories: productData.categories, // already collection handles (language-stable)
            Market: market,
            'User Agent': navigator.userAgent,
          });
        } catch (error) {
          // Error tracking Product Viewed
        }
      }
    },
  );
}
